import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/config";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  CalendarDays, MapPin, Phone, Plus, Pencil, Trash2, Check, X, RotateCcw, Loader2, Truck,
} from "lucide-react";

const PLAN_LABEL = { biweekly: "Bi-Weekly (2 cans)", monthly_service: "Monthly (2 cans)" };
const todayIso = () => new Date().toISOString().slice(0, 10);
const fmtDate = (iso) =>
  new Date(iso + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
const emptyCustomer = () => ({ name: "", phone: "", email: "", address: "", zip: "", plan: "biweekly", cans: 2, start_date: todayIso(), notes: "" });

export const ScheduleManager = () => {
  const [customers, setCustomers] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null=add, object=edit
  const [form, setForm] = useState(emptyCustomer());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, a] = await Promise.all([api.get("/admin/customers"), api.get("/admin/appointments")]);
      setCustomers(c.data); setAppointments(a.data);
    } catch (e) { /* handled by parent auth */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setForm(emptyCustomer()); setDialogOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, phone: c.phone || "", email: c.email || "", address: c.address || "",
      zip: c.zip || "", plan: c.plan, cans: c.cans, start_date: c.start_date, notes: c.notes || "" });
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Customer name is required"); return; }
    setSaving(true);
    try {
      if (editing) { await api.patch(`/admin/customers/${editing.id}`, form); toast.success("Customer updated"); }
      else { await api.post("/admin/customers", form); toast.success("Customer added & schedule generated"); }
      setDialogOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const removeCustomer = async (id) => {
    if (!window.confirm("Delete this customer and all their appointments?")) return;
    await api.delete(`/admin/customers/${id}`); toast.success("Customer removed"); load();
  };
  const setStatus = async (id, status) => { await api.patch(`/admin/appointments/${id}`, { status }); load(); };
  const removeAppt = async (id) => { await api.delete(`/admin/appointments/${id}`); load(); };

  // build a 4-week rotating view starting this week (Sunday)
  const weeks = [];
  const start = new Date(); start.setHours(0, 0, 0, 0); start.setDate(start.getDate() - start.getDay());
  for (let w = 0; w < 4; w++) {
    const ws = new Date(start); ws.setDate(start.getDate() + w * 7);
    const we = new Date(ws); we.setDate(ws.getDate() + 6);
    const wsIso = ws.toISOString().slice(0, 10), weIso = we.toISOString().slice(0, 10);
    const appts = appointments
      .filter((a) => a.date >= wsIso && a.date <= weIso)
      .sort((a, b) => (a.date + a.zip).localeCompare(b.date + b.zip));
    weeks.push({ label: `${fmtDate(wsIso)} – ${fmtDate(weIso)}`, wsIso, appts });
  }
  const today = todayIso();
  const pastAppts = appointments.filter((a) => a.date < today).sort((a, b) => b.date.localeCompare(a.date)).slice(0, 40);

  if (loading) return <div className="py-16 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-boco-navy" /></div>;

  return (
    <div className="space-y-8" data-testid="schedule-manager">
      {/* CUSTOMERS */}
      <div className="rounded-2xl bg-white border border-slate-200 shadow-sm">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-display text-lg font-extrabold text-boco-navy">Recurring Customers</h3>
            <p className="text-sm text-slate-500">Bi-weekly & monthly plans. Adding a customer auto-builds their rotating schedule.</p>
          </div>
          <button onClick={openAdd} className="inline-flex items-center gap-2 rounded-full bg-boco-green px-5 py-2.5 font-bold text-boco-navy hover:bg-boco-greendark hover:text-white transition-colors" data-testid="add-customer-btn">
            <Plus className="h-4 w-4" /> Add Customer
          </button>
        </div>
        <div className="divide-y divide-slate-100">
          {customers.map((c) => (
            <div key={c.id} className="p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`customer-row-${c.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-boco-navy">{c.name}</span>
                  <span className="rounded-full bg-boco-navy/5 px-2.5 py-0.5 text-xs font-bold text-boco-navy">{PLAN_LABEL[c.plan] || c.plan}</span>
                  <span className="text-xs text-slate-500">{c.cans} can{c.cans > 1 ? "s" : ""}</span>
                </div>
                <div className="mt-1 text-sm text-slate-500 flex flex-wrap gap-x-4 gap-y-0.5">
                  {c.address && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{c.address}{c.zip ? `, ${c.zip}` : ""}</span>}
                  {c.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{c.phone}</span>}
                </div>
              </div>
              <div className="text-sm shrink-0">
                <div className="text-xs text-slate-400 uppercase tracking-wide font-semibold">Next due</div>
                <div className="font-display font-extrabold text-boco-greendark">{c.next_due ? fmtDate(c.next_due) : "—"}</div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => openEdit(c)} className="rounded-md p-2 text-slate-500 hover:bg-slate-100 transition-colors" data-testid={`customer-edit-${c.id}`}><Pencil className="h-4 w-4" /></button>
                <button onClick={() => removeCustomer(c.id)} className="rounded-md p-2 text-red-500 hover:bg-red-50 transition-colors" data-testid={`customer-delete-${c.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
          {customers.length === 0 && <div className="p-8 text-center text-slate-400">No recurring customers yet. Add one to start scheduling.</div>}
        </div>
      </div>

      {/* 4-WEEK ROTATING SCHEDULE */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <CalendarDays className="h-5 w-5 text-boco-green" />
          <h3 className="font-display text-lg font-extrabold text-boco-navy">4-Week Rotating Schedule</h3>
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          {weeks.map((wk, wi) => (
            <div key={wk.wsIso} className={`rounded-2xl border shadow-sm ${wi === 0 ? "border-boco-green" : "border-slate-200"} bg-white`} data-testid={`week-card-${wi}`}>
              <div className={`px-5 py-3 rounded-t-2xl flex items-center justify-between ${wi === 0 ? "bg-boco-greenlight" : "bg-slate-50"}`}>
                <span className="font-display font-bold text-boco-navy">Week {wi + 1} · {wk.label}</span>
                <span className="text-xs font-semibold text-slate-500">{wk.appts.length} stop{wk.appts.length !== 1 ? "s" : ""}</span>
              </div>
              <div className="divide-y divide-slate-100">
                {wk.appts.map((a) => (
                  <div key={a.id} className="px-5 py-3 flex items-center gap-3" data-testid={`appt-${a.id}`}>
                    <div className="w-14 shrink-0 text-center">
                      <div className="text-[10px] uppercase text-slate-400 font-bold">{new Date(a.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "short" })}</div>
                      <div className="font-display font-black text-boco-navy leading-none">{new Date(a.date + "T00:00:00").getDate()}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`font-semibold truncate ${a.status === "completed" ? "text-slate-400 line-through" : "text-boco-navy"}`}>{a.customer_name}</div>
                      <div className="text-xs text-slate-500 truncate inline-flex items-center gap-1"><Truck className="h-3 w-3" />{a.address || "No address"}{a.zip ? ` · ${a.zip}` : ""}</div>
                    </div>
                    {a.status === "completed" && <span className="text-xs font-bold text-boco-greendark shrink-0">Done</span>}
                    {a.status === "skipped" && <span className="text-xs font-bold text-amber-600 shrink-0">Skipped</span>}
                    <div className="flex gap-1 shrink-0">
                      {a.status !== "completed" && <button onClick={() => setStatus(a.id, "completed")} title="Mark done" className="rounded-md p-1.5 text-boco-greendark hover:bg-boco-greenlight transition-colors" data-testid={`appt-done-${a.id}`}><Check className="h-4 w-4" /></button>}
                      {a.status === "scheduled" && <button onClick={() => setStatus(a.id, "skipped")} title="Skip" className="rounded-md p-1.5 text-amber-600 hover:bg-amber-50 transition-colors" data-testid={`appt-skip-${a.id}`}><X className="h-4 w-4" /></button>}
                      {a.status !== "scheduled" && <button onClick={() => setStatus(a.id, "scheduled")} title="Reset" className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 transition-colors" data-testid={`appt-reset-${a.id}`}><RotateCcw className="h-4 w-4" /></button>}
                      <button onClick={() => removeAppt(a.id)} title="Remove" className="rounded-md p-1.5 text-red-400 hover:bg-red-50 transition-colors"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
                {wk.appts.length === 0 && <div className="px-5 py-6 text-center text-sm text-slate-400">No cleanings scheduled.</div>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* PAST / HISTORY */}
      <div className="rounded-2xl bg-white border border-slate-200 shadow-sm">
        <div className="p-6 border-b border-slate-100">
          <h3 className="font-display text-lg font-extrabold text-boco-navy">Past Cleanings</h3>
          <p className="text-sm text-slate-500">Previous appointments and service history for reference.</p>
        </div>
        <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
          {pastAppts.map((a) => (
            <div key={a.id} className="px-6 py-3 flex items-center gap-4 text-sm" data-testid={`past-appt-${a.id}`}>
              <span className="w-28 shrink-0 text-slate-500">{fmtDate(a.date)}</span>
              <span className="flex-1 font-semibold text-boco-navy truncate">{a.customer_name}</span>
              <span className="hidden sm:block text-slate-500 truncate max-w-[220px]">{a.address}{a.zip ? ` · ${a.zip}` : ""}</span>
              <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${a.status === "completed" ? "bg-boco-greenlight text-boco-greendark" : a.status === "skipped" ? "bg-amber-50 text-amber-600" : "bg-slate-100 text-slate-500"}`}>{a.status}</span>
            </div>
          ))}
          {pastAppts.length === 0 && <div className="p-8 text-center text-slate-400">No past cleanings yet.</div>}
        </div>
      </div>

      {/* ADD / EDIT DIALOG */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white text-slate-900 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-boco-navy">{editing ? "Edit Customer" : "Add Customer"}</DialogTitle>
            <DialogDescription>Set the plan and first cleaning date — the rotating schedule is built automatically.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label className="text-boco-navy font-semibold">Name *</Label><Input className="mt-1 text-slate-900" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cust-name" /></div>
            <div><Label className="text-boco-navy font-semibold">Phone</Label><Input className="mt-1 text-slate-900" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="cust-phone" /></div>
            <div><Label className="text-boco-navy font-semibold">Email</Label><Input className="mt-1 text-slate-900" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="cust-email" /></div>
            <div className="sm:col-span-2"><Label className="text-boco-navy font-semibold">Address</Label><Input className="mt-1 text-slate-900" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="cust-address" /></div>
            <div><Label className="text-boco-navy font-semibold">ZIP</Label><Input className="mt-1 text-slate-900" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value.replace(/\D/g, "").slice(0, 5) })} data-testid="cust-zip" /></div>
            <div>
              <Label className="text-boco-navy font-semibold">Plan</Label>
              <select className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm text-slate-900" value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} data-testid="cust-plan">
                <option value="biweekly">Bi-Weekly (every 2 weeks)</option>
                <option value="monthly_service">Monthly (every 4 weeks)</option>
              </select>
            </div>
            <div><Label className="text-boco-navy font-semibold">Cans</Label><Input type="number" min="1" className="mt-1 text-slate-900" value={form.cans} onChange={(e) => setForm({ ...form, cans: parseInt(e.target.value) || 1 })} data-testid="cust-cans" /></div>
            <div><Label className="text-boco-navy font-semibold">First cleaning date</Label><Input type="date" className="mt-1 text-slate-900" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="cust-start" /></div>
            <div className="sm:col-span-2"><Label className="text-boco-navy font-semibold">Notes</Label><Input className="mt-1 text-slate-900" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Gate code, which cans, etc." data-testid="cust-notes" /></div>
            <p className="sm:col-span-2 text-xs text-slate-400">Changing the plan or first date will rebuild upcoming (future) appointments; past cleanings are kept.</p>
            <DialogFooter className="sm:col-span-2">
              <button type="submit" disabled={saving} className="w-full rounded-full bg-boco-navy py-3 font-bold text-white hover:bg-boco-navylight transition-colors disabled:opacity-70 flex items-center justify-center gap-2" data-testid="cust-save">
                {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : (editing ? "Save Changes" : "Add Customer & Build Schedule")}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
